import React, {useState, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import RWBUserImages from '../RWBUserImages';
import styles from './ProfileDetail.module.css';
import {IconButton, Grid} from '@material-ui/core';
import {useHistory, useRouteMatch} from 'react-router-dom';
import {userProfile} from '../../../../shared/models/UserProfile';
import {rwbApi} from '../../../../shared/apis/api';
import Loading from '../Loading';
import AppMark from '../../../../shared/project-info/web-project-info/svgs/AppMark';
import ChevronBackIcon from '../svgs/ChevronBackIcon';
import {ClipLoader} from 'react-spinners';
import FeedList from '../feed/FeedList';
import ExpandingText from '../ExpandingText';
import {
  logAccessFollowers,
  logAccessFollowing,
  logUnfollow,
  logFollow,
} from '../../../../shared/models/Analytics';
import {hasReachedBottom} from '../../BrowserUtil';
import {reportAPI} from '../../../../shared/apis/reportAPI';
import {errorAlert} from '../../../../shared/utils/ErrorAlert';
import {
  PRIVATE_USER_ERROR,
  REPORT_SENT,
} from '../../../../shared/constants/ErrorMessages';
import {projectConfig} from '../../../../shared/project-info/projectConfig';
import { PROFILE_TAB_LABELS } from '../../../../shared/constants/Labels';
import ChevronRightIcon from '../svgs/ChevronRightIcon';
import BlockAndReportOverlay from '../BlockAndReportOverlay';
import { formatStatusTitle } from '../../../../shared/utils/Helpers';
import { filterRemovedEvents } from '../../../../shared/utils/FeedHelpers';

export default function ProfileDetail() {
  const [isLoading, setIsLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false); // server code to see if the person is being followed
  const [isFollowed, setIsFollowed] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [preferredChapter, setPreferredChapter] = useState('');
  // const [homeChapter, setHomeChapter] = useState(''); // Currently home chapter, service type, service status, and purchase data are giving me errors.
  const [workspace, setWorkspace] = useState('');
  const [title, setTitle] = useState('');
  const [serviceInfo, setServiceInfo] = useState('');
  const [militaryRank, setMilitaryRank] = useState('');
  // const [militarySpecialty, setMilitarySpecialty] = useState('');
  const [industry, setIndustry] = useState('');
  // const [purchaseData, setPurchaseData] = useState('');
  const [profileBio, setProfileBio] = useState('');
  const [coverPhoto, setCoverPhoto] = useState('');
  const [profilePhoto, setProfilePhoto] = useState('');
  const [followingAmount, setFollowingAmount] = useState('-');
  const [followersAmount, setFollowersAmount] = useState('-');
  const [isEagleLeader, setIsEagleLeader] = useState(false);
  const [expirationTermOfService, setExpirationTermOfService] = useState(null);
  const [anonymousProfile, setAnonymousProfile] = useState(false);
  const [isLoadingFeed, setIsLoadingFeed] = useState(true);
  const [userFeed, setUserFeed] = useState([]);
  const [retrievedLastPost, setRetrievedLastPost] = useState(false);
  const [userbadges, setUserBadges] = useState([]);

  const loggedInUser = userProfile.getUserProfile();
  const {profileId} = useParams();
  const history = useHistory();
  const match = useRouteMatch();

  useEffect(() => {
    setIsLoading(true);
    retrieveUser()
      .then((user) => {
        // TODO: store all of this as a single user object?
        setIsEagleLeader(user.eagle_leader);
        setCoverPhoto(user.cover_photo_url);
        setProfilePhoto(user.profile_photo_url);
        setFirstName(user.first_name);
        setLastName(user.last_name);
        setPreferredChapter(user.preferred_chapter);
        // setHomeChapter(user.home_chapter);
        setWorkspace(user.workspace);
        setServiceInfo(user?.service_info);
        setIndustry(user.extra_info?.industry);
        // setPurchaseData(user.purchaseData.description);
        setExpirationTermOfService(user.military_ets);
        setMilitaryRank(user.military_rank);
        setProfileBio(user.profile_bio);
        setAnonymousProfile(user.anonymous_profile);
        getUsersFollowSummary().then((fData) => {
          setFollowingAmount(fData.following);
          setFollowersAmount(fData.followers);
          getFollowerRelationship().then((followed) => {
            setIsFollowed(followed);
            getFollowingRelationship().then((following) => {
              setIsFollowing(following);
              setIsLoading(false);
            });
          });
        });
        if (projectConfig.hasChallenges) {
          getBadges();
        }
        if (user.anonymous_profile) errorAlert(PRIVATE_USER_ERROR);
        else {
          getFeed()
            .then((feed) => {
              setIsLoadingFeed(false);
            })
            .catch(() => {
              setIsLoadingFeed(false);
              errorAlert('Error retrieving the user feed');
            });
        }
      })
      .catch((error) => {
        console.warn(error);
        setIsLoading(false);
        if (error.toString().includes('403')) {
          errorAlert('This user has set their profile to private.');
          history.goBack();
        } else errorAlert('There was an error retrieving the user.');
      });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', trackScrolling);
    return () => {
      window.removeEventListener('scroll', trackScrolling);
    };
  }, [userFeed]);

  useEffect(() => {
    // replacement for setState callback
    if (retrievedLastPost) handleLoadMore();
  }, [retrievedLastPost]);

  const trackScrolling = (event) => {
    event.preventDefault();
    const wrappedElement = document.getElementById('root');
    if (hasReachedBottom(wrappedElement)) {
      setRetrievedLastPost(true);
    }
  };

  const getUsersFollowSummary = () => {
    return rwbApi.getFollowSummary(profileId);
  };

  // see if this profile is following the logged in user
  const getFollowerRelationship = () => {
    return rwbApi
      .getFollowingRelationships(profileId, loggedInUser.id)
      .then((isFollowed, isFollowing) => {
        return isFollowed;
      });
  };

  // see if the logged in user is following this profile
  const getFollowingRelationship = () => {
    return rwbApi
      .getFollowingRelationships(loggedInUser.id, profileId)
      .then((isFollowing) => {
        return isFollowing;
      });
  };

  const retrieveUser = () => {
    return rwbApi.getUserByID(profileId);
  };

  const getBadges = () => {
    return rwbApi
      .getUserBadges(profileId)
      .then((result) => {
        setUserBadges(result.data.badges);
      })
      .catch((error) => {
        console.warn(error);
        errorAlert("Error retrieving user's badges.");
      });
  };

  const getFeed = () => {
    return rwbApi
      .getUserFeed(profileId)
      .then((result) => {
        if (result.data) {
          // ensures editing posts on other users' profiles is updated
          setUserFeed(filterRemovedEvents(result.data.results));
          return filterRemovedEvents(result.data.results);
        }
        return [];
      })
      .catch((error) => {
        console.warn('error getting feed: ', error);
      });
  };

  const handleLoadMore = () => {
    const lastPost = userFeed[userFeed.length - 1];
    setIsLoadingFeed(true);
    if (retrievedLastPost && lastPost) {
      rwbApi.getUserFeed(profileId, lastPost.id).then((result) => {
        if (result.data.results.length > 0) {
          setUserFeed([...userFeed, ...filterRemovedEvents(result.data.results)]);
          setIsLoadingFeed(false);
          setRetrievedLastPost(false);
        } else setIsLoadingFeed(false);
      });
    }
  };

  const updateFollowState = () => {
    setIsLoading(true);
    if (isFollowing) {
      rwbApi
        .unfollowUser(profileId)
        .then((result) => {
          setIsFollowing(false);
          setFollowersAmount(followersAmount - 1);
          logUnfollow();
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      rwbApi
        .followUser(profileId)
        .then((result) => {
          setIsFollowing(true);
          setFollowersAmount(followersAmount + 1);
          logFollow();
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const reportUser = () => {
    if (window.confirm('Would you like to report this member?')) {
      const reporterID = JSON.stringify({reporter: loggedInUser.id});
      reportAPI
        .user(profileId, reporterID)
        .then(() => {
          errorAlert(REPORT_SENT);
        })
        .catch((err) => {
          console.warn(err);
        });
    }
  };

  const navigateBadges = () =>{
    history.push(
      {
        pathname: '/challengebadges',
        state: {badgesData: userbadges},
      }
    )
  }

  return (
    <div id={'root'}>
      {isLoading && (
        <Loading size={100} color={'var(--white)'} loading={true} right />
      )}
      <div className={styles.userImageContainer}>
        <RWBUserImages coverPhoto={coverPhoto} profilePhoto={profilePhoto} />

        <div className={styles.navigation}>
          <div onClick={() => history.goBack()}>
            <IconButton edge="start" color="inherit">
              <ChevronBackIcon tintColor={'var(--primary)'} />
            </IconButton>
          </div>
          {profileId !== parseInt(loggedInUser.id) ? (
            <div style={{display: 'flex', flex: 1, justifyContent: 'flex-end'}}>
              <BlockAndReportOverlay
                loggedInUser={loggedInUser}
                profileId={profileId}
              />
            </div>
          ) : null}
        </div>
      </div>
      <div className={styles.contentContainer}>
        <div className={styles.inlineContainer}>
          {/* Name */}
          <div className={styles.nameContainer}>
            <h1>{`${firstName} ${lastName} `}</h1>
            {/* {isEagleLeader && (
              <AppMark tintColor={'var(--secondary)'} width={40} height={18} />
            )} */}
          </div>
        </div>
        {/* Chapter/Workspace */}
        {workspace || preferredChapter ?
          <h2 className={styles.chapterContainer}>
            {projectConfig.showProfileChapter ?
              (preferredChapter && preferredChapter.name) || null
              // (homeChapter && homeChapter.name)
            : workspace && workspace.name}
          </h2>
        : null}
        {/* Leadership Title */}
        {isEagleLeader && projectConfig.leaderRoleDisplay ? (
          <h5 style={{textAlign: 'center'}} className={[styles.eagleLeaderText, styles.roleContainer]}>
            {projectConfig.leaderRoleDisplay}
          </h5>
        ): null}
        {/* Industry */}
        {industry &&
          <h6 className={styles.industryContainer}>
            {/* {purchaseData ? purchaseData + ", " : null} */}
            {industry}
          </h6>
        }
        {/* Service and Branch */}
        {(serviceInfo?.status || serviceInfo?.service_type) && <h2 style={{textAlign: 'center'}}>{formatStatusTitle(serviceInfo?.status, serviceInfo?.service_type)}</h2>}
        {/* Follow */}
        <div className={styles.profileActions}>
          <div
              className={styles.followCount}
              onClick={() =>
                logAccessFollowers() +
                history.push({
                  pathname: `${match.path}/followers`,
                  state: {index: 0, selectedUserID: profileId},
                })
              }>
              <strong>{followersAmount}</strong>
              followers
            </div>
            <div
              className={styles.followCount}
              onClick={() =>
                logAccessFollowing() +
                history.push({
                  pathname: `${match.path}/followers`,
                  state: {index: 1, selectedUserID: profileId},
                })
              }>
              <strong>{followingAmount}</strong>
              following
            </div>
          </div>
        {isFollowed && (
              <h5 className={styles.followsYouText}>{firstName} follows you</h5>
            )}
        <div className={styles.profileActions}>
          {!anonymousProfile && loggedInUser.id !== parseInt(profileId) && (
            <div
              className={`${styles.followButton} ${
                isFollowing && styles.followButtonFollowed
              }`}
              onClick={updateFollowState}>
              <div>{isFollowing ? 'Unfollow' : 'Follow'}</div>
            </div>
          )}
          
        </div>
        
        <br />
        <ExpandingText text={profileBio} />
      </div>

      {projectConfig.hasChallenges && userbadges.length > 0 && (
          <div className={[styles.contentContainer]}>
            {/* badges view */}
            <div className={[styles.contentContainerDiv]}>
              <div onClick={() => { userbadges.length > 0 && navigateBadges() }} className={[styles.metricBox]}>
                <p className={styles.metricLink}>{PROFILE_TAB_LABELS.CHALLENGE_BADGES}</p>
                {userbadges.length > 0 && <div>
                  <ChevronRightIcon />
                </div>
                }
              </div>
              <div>
                <div onClick={() => { navigateBadges() }} className={[styles.imageContainerFlex]}>
                  {userbadges.map((badge, index) => {
                    return (index < 6) ?
                      <img
                        key={index}
                        src={badge.image_url}
                        alt="badge"
                      />
                    : null
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

      <FeedList userFeed={userFeed} origin={'user'} mergeNewPost={getFeed} />
      <Grid container justifyContent={'center'}>
        <ClipLoader size={60} color={'var(--grey20)'} loading={isLoadingFeed} />
      </Grid>
    </div>
  );
}
